/**
 * Reusable form fields for the feedback modal:
 *   - Field      : label + hint wrapper.
 *   - MultiSelectSearch : searchable multi-select with removable chips,
 *     optional "create new" for free-form entries (tags).
 *   - VideoList  : repeatable url + description rows for proposed
 *     video additions.
 *
 * Kept separate from FeedbackModal so that file stays focused on the
 * branching form orchestration.
 */
import { useMemo, useRef, useState } from 'react';
import { normalizeForSearch, searchMatcher } from './search-text';

export type Option = { value: string; label: string; group?: string };

// ---- shared styles ----

export const inputStyle: React.CSSProperties = {
  width: '100%',
  background: 'rgba(0,0,0,0.3)',
  border: '1px solid rgba(255,255,255,0.14)',
  borderRadius: 6,
  color: '#e6e8ef',
  fontSize: 13,
  padding: '8px 10px',
  outline: 'none',
  fontFamily: 'inherit',
};

export const chipStyle: React.CSSProperties = {
  padding: '5px 12px',
  borderRadius: 14,
  border: '1px solid rgba(255,255,255,0.12)',
  fontSize: 12,
  cursor: 'pointer',
  transition: 'background 120ms ease, border-color 120ms ease',
  background: 'rgba(255,255,255,0.05)',
  color: '#cad3df',
};

export function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <div style={{ marginBottom: 14 }}>
      <div
        style={{
          fontSize: 11,
          letterSpacing: 0.4,
          textTransform: 'uppercase',
          opacity: 0.6,
          marginBottom: 6,
        }}
      >
        {label}
      </div>
      {children}
      {hint && (
        <div style={{ fontSize: 11, opacity: 0.45, marginTop: 5 }}>{hint}</div>
      )}
    </div>
  );
}

// ---- MultiSelectSearch ----

export function MultiSelectSearch({
  options,
  selected,
  onChange,
  placeholder,
  allowCreate = false,
  maxVisible = 8,
}: {
  options: Option[];
  selected: string[];
  onChange: (next: string[]) => void;
  placeholder?: string;
  /** Allow adding a typed value that isn't in `options` (e.g. new tag). */
  allowCreate?: boolean;
  maxVisible?: number;
}) {
  const [query, setQuery] = useState('');
  const [focused, setFocused] = useState(false);
  const blurTimer = useRef<number | null>(null);

  const byValue = useMemo(
    () => new Map(options.map((o) => [o.value, o])),
    [options],
  );

  const filtered = useMemo(() => {
    const notSelected = options.filter((o) => !selected.includes(o.value));
    // Folded emptiness, so a query of pure punctuation keeps the capped
    // default list rather than falling through to the full filter.
    if (!normalizeForSearch(query)) return notSelected.slice(0, maxVisible);
    const match = searchMatcher(query);
    return notSelected
      .filter((o) => match(o.label) || match(o.value) || match(o.group))
      .slice(0, maxVisible);
  }, [options, selected, query, maxVisible]);

  const add = (value: string) => {
    if (!value || selected.includes(value)) return;
    onChange([...selected, value]);
    setQuery('');
  };
  const remove = (value: string) => {
    onChange(selected.filter((v) => v !== value));
  };

  // "Create" is offered when allowCreate is on, there's a query, and it
  // doesn't exactly match an existing option label/value.
  const trimmed = query.trim();
  const canCreate =
    allowCreate &&
    trimmed.length > 0 &&
    !options.some(
      (o) =>
        o.label.toLowerCase() === trimmed.toLowerCase() ||
        o.value.toLowerCase() === trimmed.toLowerCase(),
    ) &&
    !selected.includes(trimmed);

  return (
    <div style={{ position: 'relative' }}>
      {/* Selected chips */}
      {selected.length > 0 && (
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 5,
            marginBottom: 6,
          }}
        >
          {selected.map((v) => {
            const opt = byValue.get(v);
            return (
              <span
                key={v}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 5,
                  padding: '3px 6px 3px 10px',
                  borderRadius: 12,
                  fontSize: 12,
                  background: 'rgba(155,231,255,0.14)',
                  border: '1px solid rgba(155,231,255,0.32)',
                  color: '#cdf6ff',
                }}
              >
                {opt?.label ?? v}
                <button
                  type="button"
                  onClick={() => remove(v)}
                  aria-label={`Remove ${opt?.label ?? v}`}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: 'inherit',
                    cursor: 'pointer',
                    fontSize: 14,
                    lineHeight: 1,
                    padding: 0,
                    opacity: 0.8,
                  }}
                >
                  ×
                </button>
              </span>
            );
          })}
        </div>
      )}

      <input
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        onFocus={() => setFocused(true)}
        onBlur={() => {
          // Delay so an option click registers before the list hides.
          blurTimer.current = window.setTimeout(() => setFocused(false), 120);
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            if (filtered.length > 0) add(filtered[0].value);
            else if (canCreate) add(trimmed);
          }
        }}
        placeholder={placeholder}
        style={inputStyle}
      />

      {/* Dropdown */}
      {focused && (filtered.length > 0 || canCreate) && (
        <div
          onMouseDown={() => {
            // Cancel the blur-close so the click below lands.
            if (blurTimer.current) window.clearTimeout(blurTimer.current);
          }}
          style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            marginTop: 4,
            zIndex: 5,
            background: 'rgba(18,20,28,0.98)',
            border: '1px solid rgba(255,255,255,0.14)',
            borderRadius: 8,
            boxShadow: '0 10px 28px rgba(0,0,0,0.5)',
            maxHeight: 220,
            overflowY: 'auto',
            padding: 4,
          }}
        >
          {filtered.map((o) => (
            <button
              type="button"
              key={o.value}
              onClick={() => add(o.value)}
              style={dropdownItem}
            >
              <span>{o.label}</span>
              {o.group && (
                <span style={{ fontSize: 10, opacity: 0.45 }}>{o.group}</span>
              )}
            </button>
          ))}
          {canCreate && (
            <button
              type="button"
              onClick={() => add(trimmed)}
              style={{ ...dropdownItem, color: '#9cf2c4' }}
            >
              + Add “{trimmed}”
            </button>
          )}
        </div>
      )}
    </div>
  );
}

const dropdownItem: React.CSSProperties = {
  display: 'flex',
  width: '100%',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 8,
  background: 'none',
  border: 'none',
  color: '#e6e8ef',
  fontSize: 13,
  textAlign: 'left',
  padding: '7px 8px',
  borderRadius: 6,
  cursor: 'pointer',
};

// ---- VideoList ----

export type VideoEntry = { url: string; description: string };

export function VideoList({
  videos,
  onChange,
}: {
  videos: VideoEntry[];
  onChange: (next: VideoEntry[]) => void;
}) {
  const update = (i: number, patch: Partial<VideoEntry>) => {
    onChange(videos.map((v, idx) => (idx === i ? { ...v, ...patch } : v)));
  };
  const add = () => onChange([...videos, { url: '', description: '' }]);
  const remove = (i: number) => onChange(videos.filter((_, idx) => idx !== i));

  return (
    <div>
      {videos.map((v, i) => (
        <div
          key={i}
          style={{
            border: '1px solid rgba(255,255,255,0.1)',
            borderRadius: 8,
            padding: 10,
            marginBottom: 8,
            background: 'rgba(255,255,255,0.02)',
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              marginBottom: 6,
            }}
          >
            <input
              value={v.url}
              onChange={(e) => update(i, { url: e.target.value })}
              placeholder="https://youtube.com/…  (link)"
              style={{ ...inputStyle, flex: 1 }}
            />
            <button
              type="button"
              onClick={() => remove(i)}
              aria-label="Remove video"
              style={{
                flexShrink: 0,
                background: 'rgba(255,128,140,0.12)',
                border: '1px solid rgba(255,128,140,0.3)',
                color: '#ffc0c8',
                borderRadius: 6,
                width: 30,
                height: 30,
                cursor: 'pointer',
                fontSize: 15,
                lineHeight: 1,
              }}
            >
              ×
            </button>
          </div>
          <textarea
            value={v.description}
            onChange={(e) => update(i, { description: e.target.value })}
            placeholder="What does this clip show? Include a timestamp (e.g. 1:24) if it starts mid-video."
            rows={2}
            style={{ ...inputStyle, resize: 'vertical', minHeight: 48 }}
          />
        </div>
      ))}
      <button
        type="button"
        onClick={add}
        style={{
          ...chipStyle,
          display: 'inline-flex',
          alignItems: 'center',
          gap: 5,
        }}
      >
        + Add {videos.length === 0 ? 'a video' : 'another'}
      </button>
    </div>
  );
}
